import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Box,
  TextField,
  Select,
  MenuItem,
  List,
  ListItem,
  Checkbox,
  Divider,
  Paper,
  CircularProgress
} from "@mui/material";
import { db, auth } from "../../config/firebase"; // Adjust the import path as necessary
import { collection, addDoc, getDocs, doc, setDoc, getDoc, query, where } from "firebase/firestore";
import { useParams, useNavigate } from "react-router-dom";


const CollegeDashboard = () => {
  const { collegeId: paramCollegeId } = useParams();
  const [collegeId, setCollegeId] = useState(paramCollegeId || null);
  const [className, setClassName] = useState("");
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedTeacher, setSelectedTeacher] = useState("");
  const [assignedTeachers, setAssignedTeachers] = useState([]);
  const [check, setChecked] = useState(false);
  const [open, setOpen] = useState(true);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchCollegeId = async (userId) => {
      try {
        const q = query(collection(db, "colleges"), where("adminId", "==", userId));
        const querySnapshot = await getDocs(q);
        if (!querySnapshot.empty) {
          const collegeDoc = querySnapshot.docs[0];
          setCollegeId(collegeDoc.id);
        } else {
          console.error("No college found for admin:", userId);
          navigate("/login");
        }
      } catch (error) {
        console.error("Error fetching college ID:", error);
      } finally {
        setLoading(false);
      }
    };

    if (!paramCollegeId) {
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        if (user) {
          await fetchCollegeId(user.uid);
        } else {
          navigate("/login");
        }
      });
      return () => unsubscribe();
    } else {
      setLoading(false);
    }
  }, [paramCollegeId, navigate]);

  useEffect(() => {
    if (!collegeId) return;

    const fetchClasses = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "colleges", collegeId, "classes"));
        const classList = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setClasses(classList);
      } catch (error) {
        console.error("Error fetching classes:", error);
      }
    };

    const fetchTeachers = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "colleges", collegeId, "teachers"));
        const teacherList = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setTeachers(teacherList);
      } catch (error) {
        console.error("Error fetching teachers:", error);
      }
    };

    fetchClasses();
    fetchTeachers();
  }, [collegeId]);

  const fetchAssignedTeachers = async (classId) => {
    if (!collegeId || !classId) return;

    try {
      const querySnapshot = await getDocs(collection(db, "colleges", collegeId, "classes", classId, "teachers"));
      const assignedList = querySnapshot.docs.map((doc) => doc.data());
      setAssignedTeachers(assignedList);
    } catch (error) {
      console.error("Error fetching assigned teachers:", error);
    }
  };

  const handleClassSelection = (classId) => {
    setSelectedClass(classId);
    fetchAssignedTeachers(classId);
  };

  const createClass = async () => {
    if (!className.trim()) {
      alert("Class name cannot be empty!");
      return;
    }
    if (!collegeId) {
      alert("College ID is missing!");
      return;
    }

    try {
      const docRef = await addDoc(collection(db, "colleges", collegeId, "classes"), { name: className });
      const newClass = { id: docRef.id, name: className };
      setClasses([...classes, newClass]);
      setClassName("");
    } catch (error) {
      console.error("Error adding class:", error);
      alert("Failed to add class.");
    }
  };

  const assignTeacher = async () => {
    if (!selectedClass || !selectedTeacher) {
      alert("Select a class and a teacher first!");
      return;
    }
    if (!collegeId) {
      alert("College ID is missing!");
      return;
    }

    const teacherRef = doc(db, "colleges", collegeId, "teachers", selectedTeacher);
    const teacherData = teachers.find((teacher) => teacher.id === selectedTeacher);
    
    if (!teacherData) {
      alert("Selected teacher not found!");
      return;
    }

    try {
      // Assign teacher to class
      await setDoc(
        doc(db, "colleges", collegeId, "classes", selectedClass, "teachers", selectedTeacher),
        {
          teacherId: selectedTeacher,
          teacherName: teacherData.fullName,
          assignedAt: new Date(),
        }
      );

      // Update teacher document with assignedClasses
      const teacherDoc = await getDoc(teacherRef);
      const assignedClasses = teacherDoc.exists() ? teacherDoc.data().assignedClasses || [] : [];

      if (!assignedClasses.includes(selectedClass)) {
        await setDoc(
          teacherRef,
          {
            assignedClasses: [...assignedClasses, selectedClass],
          },
          { merge: true }
        );
      }

      fetchAssignedTeachers(selectedClass);
      setSelectedTeacher("");
    } catch (error) {
      console.error("Error assigning teacher:", error);
      alert("Failed to assign teacher.");
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
<Dialog
  open={open}
  onClose={() => setOpen(false)}
  maxWidth="md"
  fullWidth
  className="form__dialog"
  PaperProps={{
    sx: {
      borderRadius: 3,
      boxShadow: '0px 6px 24px rgba(0, 0, 0, 0.15)',
      p: { xs: 2, md: 4 }
    }
  }}
>
  <DialogContent>
    <Typography variant="h5" fontWeight={600} color="primary.main" gutterBottom>
      College Class Management
    </Typography>

    <Divider sx={{ my: 3 }} />

    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        gap: 4
      }}
    >
      {/* Left Panel */}
      <Paper elevation={3} sx={{ p: 3, flex: 1, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={500} gutterBottom>
          Create a New Class
        </Typography>

        <TextField
          label="Class Name"
          value={className}
          onChange={(e) => setClassName(e.target.value)}
          fullWidth
          margin="normal"
          size="small"
        />

        <Button
          onClick={createClass}
          variant="contained"
          fullWidth
          sx={{ mt: 2, textTransform: 'none', fontWeight: 500 }}
        >
          Add Class
        </Button>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" fontWeight={500} gutterBottom>
          Existing Classes
        </Typography>

        <Select
          value={selectedClass}
          onChange={(e) => handleClassSelection(e.target.value)}
          fullWidth
          displayEmpty
          size="small"
        >
          <MenuItem value="" disabled>
            Select a class
          </MenuItem>
          {classes.map((cls) => (
            <MenuItem key={cls.id} value={cls.id}>
              {cls.name}
            </MenuItem>
          ))}
        </Select>

        {selectedClass && (
          <>
            <Typography variant="subtitle2" fontWeight={500} sx={{ mt: 2, mb: 1 }}>
              Assigned Teachers:
            </Typography>

            {assignedTeachers.length > 0 ? (
              <List dense sx={{ maxHeight: 150, overflowY: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                {assignedTeachers.map((teacher, index) => (
                  <ListItem key={index}>{teacher.teacherName}</ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No teachers assigned yet
              </Typography>
            )}
          </>
        )}
      </Paper>

      {/* Right Panel */}
      <Paper elevation={3} sx={{ p: 3, flex: 1, borderRadius: 3 }}>
        <Typography variant="h6" fontWeight={500} gutterBottom>
          Assign Teacher to Class
        </Typography>

        {selectedClass ? (
          <>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Selected Class: <strong>{classes.find((c) => c.id === selectedClass)?.name}</strong>
            </Typography>

            <Select
              value={selectedTeacher}
              onChange={(e) => setSelectedTeacher(e.target.value)}
              fullWidth
              displayEmpty
              size="small"
            >
              <MenuItem value="" disabled>
                Select a teacher
              </MenuItem>
              {teachers
                .filter((teacher) => !assignedTeachers.some((t) => t.teacherId === teacher.id))
                .map((teacher) => (
                  <MenuItem key={teacher.id} value={teacher.id}>
                    {teacher.fullName}
                  </MenuItem>
                ))}
            </Select>

            <Button
              onClick={assignTeacher}
              variant="contained"
              fullWidth
              sx={{ mt: 2, textTransform: 'none', fontWeight: 500 }}
              disabled={!selectedTeacher}
            >
              Assign Teacher
            </Button>
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Please select a class to assign teachers
          </Typography>
        )}

        <Divider sx={{ my: 3 }} />

        <Box display="flex" alignItems="center" gap={1}>
          <Checkbox
            color="primary"
            checked={check}
            onChange={() => setChecked(!check)}
            size="small"
          />
          <Typography variant="body2">
            I confirm all information provided is accurate
          </Typography>
        </Box>
      </Paper>
    </Box>
  </DialogContent>

  <DialogActions sx={{ p: 3 }}>
    <Button onClick={() => setOpen(false)} sx={{ color: 'text.secondary', textTransform: 'none' }}>
      Cancel
    </Button>
    <Button
      variant="contained"
      color="primary"
      disabled={!check}
      sx={{ textTransform: 'none' }}
      onClick={() => {
        setOpen(false);
        // you can redirect or perform further action here
      }}
    >
      Continue
    </Button>
  </DialogActions>
</Dialog>

  );
};

export default CollegeDashboard;